import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, PlayCircle } from 'lucide-react';
import { glossaryService, type GlossaryTermDetail } from '../services/glossaryService';

const DIFFICULTY_STYLES: Record<string, string> = {
  starter: 'text-teal-400 bg-teal-500/10',
  intermediate: 'text-amber-400 bg-amber-500/10',
  advanced: 'text-red-400 bg-red-500/10',
};

export default function GlossaryTermPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [term, setTerm] = useState<GlossaryTermDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    glossaryService.getTermBySlug(slug)
      .then(setTerm)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="p-6 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/knowledge/glossary')}
          className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm mb-6 transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Glossary
        </button>

        {loading && (
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-700/50 rounded w-1/2" />
            <div className="h-4 bg-slate-700/50 rounded w-full" />
            <div className="h-4 bg-slate-700/50 rounded w-5/6" />
            <div className="h-4 bg-slate-700/50 rounded w-4/6" />
          </div>
        )}

        {!loading && notFound && (
          <p className="text-slate-500 text-sm">Term not found.</p>
        )}

        {!loading && term && (
          <article>
            <div className="flex items-start gap-3 mb-4">
              <h1 className="text-2xl font-bold text-white">{term.term}</h1>
              {term.difficulty_hint && (
                <span className={`mt-1 text-xs px-2.5 py-1 rounded-full capitalize ${DIFFICULTY_STYLES[term.difficulty_hint] ?? ''}`}>
                  {term.difficulty_hint}
                </span>
              )}
            </div>

            <p className="text-slate-300 leading-relaxed mb-8 whitespace-pre-wrap">{term.definition}</p>

            {term.see_also_video && (
              <div className="mb-6 p-4 rounded-xl bg-slate-800/70 border border-slate-700/50 flex items-center gap-3">
                <PlayCircle size={16} className="text-teal-400 shrink-0" />
                <span className="text-sm text-slate-400">Watch:</span>
                <button
                  onClick={() => navigate(`/knowledge/${term.see_also_video!.id}`)}
                  className="text-sm text-teal-400 hover:text-teal-300 transition-colors font-medium"
                >
                  {term.see_also_video.title}
                </button>
              </div>
            )}

            {term.related_terms.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
                  Related Terms
                </h2>
                <div className="flex flex-wrap gap-2">
                  {term.related_terms.map(r => (
                    <button
                      key={r.slug}
                      onClick={() => navigate(`/knowledge/glossary/${r.slug}`)}
                      className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-sm text-slate-300 hover:border-teal-500/50 hover:text-teal-400 transition-colors"
                    >
                      {r.term}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </article>
        )}
    </div>
  );
}
